import { useState } from 'react'
import { readConsent, writeConsent, type ConsentChoice } from '../lib/consent'

/**
 * Cookie consent banner. Shows until the visitor accepts or declines, then
 * stays gone (the choice lives in localStorage).
 *
 * Declining is a real decline: analytics is gated on the stored choice in
 * `initAnalytics`, so nothing loads PostHog, GA, or GTM without an accept.
 * Accepting boots analytics right away rather than waiting for a reload, so the
 * pageview that produced the consent is not lost.
 *
 * Reads localStorage, so App renders it inside <ClientOnly>.
 */
export function CookieBanner() {
  const [choice, setChoice] = useState<ConsentChoice | null>(() => readConsent())

  if (choice) return null

  const decide = (next: ConsentChoice) => {
    writeConsent(next)
    setChoice(next)
    if (next === 'accepted') {
      void import('../lib/analytics').then(({ initAnalytics }) => initAnalytics())
    }
  }

  return (
    <div
      role="dialog"
      aria-labelledby="cookie-banner-title"
      className="fixed inset-x-4 bottom-4 z-50 md:inset-x-auto md:bottom-6 md:left-6 md:max-w-[400px]"
    >
      <div className="card p-5 shadow-[0_18px_48px_-24px_rgba(20,20,45,0.45)]">
        <h2 id="cookie-banner-title" className="text-[15px] font-semibold text-ink-950">
          Cookies
        </h2>
        <p className="mt-2 text-[13.5px] leading-[1.55] text-ink-600">
          We use cookies to analyse traffic and improve your experience. Accept to allow
          analytics cookies, or decline to keep only what&rsquo;s needed.{' '}
          <a
            href="/legal/privacy-policy"
            className="font-medium text-ink-800 underline underline-offset-2 hover:text-ink-950"
          >
            Privacy policy
          </a>
          .
        </p>
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={() => decide('accepted')}
            className="btn-primary flex-1 px-4 py-2.5 text-[13px]"
          >
            Accept
          </button>
          <button
            type="button"
            onClick={() => decide('declined')}
            className="btn-secondary flex-1 px-4 py-2.5 text-[13px]"
          >
            Decline
          </button>
        </div>
      </div>
    </div>
  )
}
